import { Trans, useLingui } from '@lingui/react/macro';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { canvasTemplateTypeFor } from 'modules/manager/domain/media-kind';
import {
  TEMPLATE_NAME_CONFLICT_MESSAGE,
  isTemplateNameConflict,
  normalizeTemplateName,
} from 'modules/manager/domain/template-name-conflict';
import { TEMPLATE_RESOLUTIONS, resolveTemplateResolution } from 'modules/manager/domain/template-resolutions';
import { useUpdateMediaManagerTemplateMutation } from 'modules/manager/store/media-manager-api';
import { useCallback, useMemo, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import MediaManagerTemplateForm, {
  DEFAULT_RESOLUTION_OPTION,
  MediaManagerTemplateFormValues,
  buildInitialSubtype,
  getDefaultMediaKindOption,
  getMediaKindOption,
  getResolutionOption,
  getSportTypeOption,
} from 'modules/manager/ui/components/forms/media-manager-template-form';

interface Args {
  templates: MediaManagerTemplate[];
}

/** The template's group refetches by the mutation's tags (IMGM-1515). */
function useEditTemplateModal({ templates }: Args) {
  const { t } = useLingui();
  const [updateTemplate, { isLoading }] = useUpdateMediaManagerTemplateMutation();
  const [template, setTemplate] = useState<MediaManagerTemplate | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((next: MediaManagerTemplate) => {
    setError(null);
    setTemplate(next);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  // Keeping its own name is not a conflict — compared the way the DB compares.
  const existingNames = useMemo(
    () =>
      templates
        .map((item) => item.name.trim())
        .filter((name) => normalizeTemplateName(name) !== normalizeTemplateName(template?.name ?? '')),
    [templates, template],
  );

  const onSubmit = useCallback(
    async (values: MediaManagerTemplateFormValues) => {
      if (!template) return;
      setError(null);
      try {
        const resolution = TEMPLATE_RESOLUTIONS[values.resolution.value];
        await updateTemplate({
          id: template.id,
          name: values.name.trim(),
          width: resolution.width,
          height: resolution.height,
          sportType: values.sportType?.value ?? '',
          mediaKind: values.mediaKind.value,
          isDefaultForKind: values.isDefaultForKind,
          templateSubtypes: values.subtype ? [values.subtype.value] : [],
        }).unwrap();
        close();
      } catch (err) {
        console.error('Failed to update media manager template:', err);
        setError(isTemplateNameConflict(err) ? t(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось сохранить шаблон, попробуйте ещё раз`);
      }
    },
    [template, updateTemplate, close, t],
  );

  const derivedType = template ? canvasTemplateTypeFor(template.mediaKind) : null;

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type="title-3" className="mb-5">
          <Trans>Редактировать шаблон</Trans>
        </UDText>
        <MediaManagerTemplateForm
          initialValues={{
            name: template?.name ?? '',
            resolution: template
              ? getResolutionOption(resolveTemplateResolution(template.width, template.height).key)
              : DEFAULT_RESOLUTION_OPTION,
            sportType: getSportTypeOption(template?.sportType ?? null),
            mediaKind: template ? getMediaKindOption(template.mediaKind) : getDefaultMediaKindOption(),
            isDefaultForKind: template?.isDefaultForKind ?? false,
            subtype: template && derivedType ? buildInitialSubtype(derivedType, template.templateSubtypes) : null,
          }}
          existingNames={existingNames}
          isLoading={isLoading}
          errorMessage={error}
          canvasObjects={template?.objects}
          submitLabel={t`Сохранить`}
          onSubmit={onSubmit}
          enableReinitialize
        />
      </Modal>
    ),
  };
}

export default useEditTemplateModal;
